import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";

import { User, AuthContextType, GroupOption } from "../types";
import { apiService } from "../services/api";
import { LocalStorageManager } from "../services/localStorage";
import { GroupCacheService } from "../services/groupCache";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  
  const [user, setUser] = useState<User | null>(null);
  const [currentGroupSlug, setCurrentGroupSlug] = useState<string | null>(null);
  const [availableGroups, setAvailableGroups] = useState<GroupOption[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [cachedGroupData, setCachedGroupData] = useState<{slug: string, data: any} | null>(null);

  // Refs for preventing duplicate calls
  const sessionCheckTimeoutRef = useRef<NodeJS.Timeout>();
  const groupsLoadTimeoutRef = useRef<NodeJS.Timeout>();
  const checkingSessionRef = useRef(false);
  const loadingGroupsRef = useRef(false);



  const clearAuthState = useCallback(() => {
    setUser(null);
    setCurrentGroupSlug(null);
    setAvailableGroups([]);
    setIsAuthenticated(false);
    setCachedGroupData(null);
    // Clear all caches when clearing auth state
    GroupCacheService.clearAllCaches();
  }, []);

  const checkAuthStatus = useCallback(async () => {
    if (hasCheckedAuth || checkingSessionRef.current) {
      return;
    }

    // Clear any existing timeout
    if (sessionCheckTimeoutRef.current) {
      clearTimeout(sessionCheckTimeoutRef.current);
    }

    // Debounce the session check
    sessionCheckTimeoutRef.current = setTimeout(async () => {
      if (checkingSessionRef.current) return;
      
      try {
        checkingSessionRef.current = true;
        setHasCheckedAuth(true);
        // Try to get current user session from backend
        const response = await apiService.getCurrentSession();
        if (response.user) {
          setUser(response.user);
          setCurrentGroupSlug(response.currentGroupSlug || null);
          setIsAuthenticated(true);
          
          // Load user's groups after authentication
          // Note: We'll trigger loadUserGroups via useEffect when user changes
        }
      } catch (error: any) {
        // No valid session found
        clearAuthState();
      } finally {
        setIsLoading(false);
        checkingSessionRef.current = false;
      }
    }, 100); // 100ms debounce
  }, [hasCheckedAuth, clearAuthState]);

  // Load user's available groups with caching
  const loadUserGroups = useCallback(async () => {
    if (!user || loadingGroupsRef.current) return;

    // Clear any existing timeout
    if (groupsLoadTimeoutRef.current) {
      clearTimeout(groupsLoadTimeoutRef.current);
    }

    // Debounce the groups loading
    groupsLoadTimeoutRef.current = setTimeout(async () => {
      if (loadingGroupsRef.current) return;
      
      try {
        loadingGroupsRef.current = true;
        // Check cache first
        const cachedGroups = GroupCacheService.getCachedUserGroups(user.id);
        if (cachedGroups) {
          setAvailableGroups(cachedGroups);
          return;
        }

        // Fetch from API if not cached
        const response = await apiService.getUserGroups();
        const groups: GroupOption[] = response.groups || [];
        
        // Cache the groups
        GroupCacheService.cacheUserGroups(user.id, groups);
        setAvailableGroups(groups);
      } catch (error) {
        console.error('Failed to load user groups:', error);
        // Don't clear auth state on group loading failure
      } finally {
        loadingGroupsRef.current = false;
      }
    }, 150); // 150ms debounce
  }, [user, currentGroupSlug]);

  // Check for existing session on app load (but not on public pages)
  useEffect(() => {
    if (!hasCheckedAuth) {
      const { isPublicRoute } = require('../constants/routes');
      
      if (isPublicRoute(window.location.pathname)) {
        // On public pages, just set loading to false without checking auth
        // Don't set hasCheckedAuth to true - let other pages trigger the check
        setIsLoading(false);
      } else {
        // On protected pages, check auth status
        checkAuthStatus();
      }
    }
  }, [hasCheckedAuth, checkAuthStatus]);

  // Load user groups when user is authenticated
  useEffect(() => {
    if (user && isAuthenticated) {
      loadUserGroups();
    }
    
    // Cleanup timeouts on unmount
    return () => {
      if (sessionCheckTimeoutRef.current) {
        clearTimeout(sessionCheckTimeoutRef.current);
      }
      if (groupsLoadTimeoutRef.current) {
        clearTimeout(groupsLoadTimeoutRef.current);
      }
    };
  }, [user, isAuthenticated, loadUserGroups]);



  const login = useCallback(async (email: string) => {
    setIsLoading(true);
    try {
      await apiService.login({ email });
      // Login just sends the magic link, actual authentication happens via magic link
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle authentication after magic link verification
  const handleAuthenticationSuccess = useCallback(async (authData: { user: User; groups: GroupOption[] }) => {
    setUser(authData.user);
    setAvailableGroups(authData.groups);
    setIsAuthenticated(true);

    // Determine which group to select
    if (authData.groups.length === 1) {
      // Single group user - automatically select it
      const groupSlug = authData.groups[0].slug;
      setCurrentGroupSlug(groupSlug);
      LocalStorageManager.setLastSelectedGroup(authData.user.id, groupSlug);
      return { redirectTo: `/${groupSlug}/dashboard` };
    } else if (authData.groups.length > 1) {
      // Multi-group user - always show group selection so they can see all their groups
      return { redirectTo: '/group-selection' };
    } else {
      // No groups - this shouldn't happen in normal flow
      throw new Error('User has no group memberships');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const switchGroup = useCallback(async (groupSlug: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Note: Access validation is handled by the backend
    // We trust that if the user got to this point, they have access

    // Update current group slug
    setCurrentGroupSlug(groupSlug);
    
    // Save the selected group to localStorage for future sessions
    LocalStorageManager.setLastSelectedGroup(user.id, groupSlug);
    
    // Clear cached group data since we're switching groups
    setCachedGroupData(null);
    
    // Clear group details cache for the previous group to ensure fresh data
    GroupCacheService.clearGroupDetailsCache();
  }, [user, availableGroups]);

  // Helper function to get the main person for the current user with caching
  const getUserMainPerson = useCallback(async () => {
    if (!user || !currentGroupSlug) return null;

    try {
      // Check cache first
      let groupData = GroupCacheService.getCachedGroupDetails(currentGroupSlug);
      
      if (!groupData) {
        // Use legacy cached data if available
        if (cachedGroupData && cachedGroupData.slug === currentGroupSlug) {
          groupData = cachedGroupData.data;
        } else {
          groupData = await apiService.getGroupDetails(currentGroupSlug);
          
          // Cache the group details
          GroupCacheService.cacheGroupDetails(currentGroupSlug, groupData);
          setCachedGroupData({ slug: currentGroupSlug, data: groupData });
        }
      }
      
      // Find the person that belongs directly to the user (not a child)
      const mainPerson = groupData.persons.find(
        (p: any) => p.userId === user.id && !p.parentPersonId
      );
      return mainPerson || null;
    } catch (error) {
      console.error("Failed to get user main person:", error);
      return null;
    }
  }, [user, currentGroupSlug, cachedGroupData]);

  const value: AuthContextType = useMemo(
    () => ({
      user,
      currentGroupSlug,
      availableGroups,
      isAuthenticated,
      isLoading,
      hasCheckedAuth,
      login,
      logout,
      switchGroup,
      getUserMainPerson,
      handleAuthenticationSuccess,
      loadUserGroups,
      checkAuthStatus,
    }),
    [
      user,
      currentGroupSlug,
      availableGroups,
      isAuthenticated,
      isLoading,
      login,
      logout,
      switchGroup,
      getUserMainPerson,
      handleAuthenticationSuccess,
      loadUserGroups,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
